import React, { FC, useEffect, useCallback } from "react";
import styled from "styled-components";

type ModalProps = { content: string; onClose: () => void };

// SSRセーフなiOS判定関数（呼ばれた時点でだけ実行）
const isIOS = (): boolean => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isiOSDevice = /iP(ad|hone|od)/.test(ua);
  const isTouchMac = /Macintosh/.test(ua) && "ontouchend" in window.document;
  return isiOSDevice || isTouchMac;
};

const lockBodyScroll = () => {
  if (typeof window === "undefined") return; // SSRガード
  const scrollY = window.scrollY || window.document.documentElement.scrollTop;
  window.document.body.dataset.scrollY = String(scrollY);

  if (isIOS()) {
    // iOSは fixed を使わず overflow hidden ロック
    window.document.body.style.overflow = "hidden";
  } else {
    window.document.body.style.position = "fixed";
    window.document.body.style.top = `-${scrollY}px`;
    window.document.body.style.left = "0";
    window.document.body.style.right = "0";
    window.document.body.style.width = "100%";
  }
};

const unlockBodyScroll = () => {
  if (typeof window === "undefined") return; // SSRガード
  const y = Number(window.document.body.dataset.scrollY || 0);

  if (isIOS()) {
    window.document.body.style.overflow = "";
  } else {
    window.document.body.style.position = "";
    window.document.body.style.top = "";
    window.document.body.style.left = "";
    window.document.body.style.right = "";
    window.document.body.style.width = "";
  }
  delete window.document.body.dataset.scrollY;
  window.scrollTo(0, y);
};

const Modal: FC<ModalProps> = ({ content, onClose }) => {
  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    lockBodyScroll();
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", onKeyDown);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("keydown", onKeyDown);
      }
      unlockBodyScroll();
    };
  }, [onKeyDown]);

  return (
    <ModalOverlay onClick={onClose} role="presentation">
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-label="お知らせモーダル"
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton
          type="button"
          onClick={onClose}
          onTouchEnd={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="閉じる"
        >
          ×
        </CloseButton>
        <ModalBody dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, "<br />") }} />
      </ModalContent>
    </ModalOverlay>
  );
};

export default Modal;

/* styles そのまま */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex; justify-content: center; align-items: center;
  z-index: 9999;
  overscroll-behavior: contain;
  touch-action: pan-y;
`;

const ModalContent = styled.div`
  background: white;
  width: 50%;
  max-height: 90vh;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0,0,0,.1);
  position: relative;
  padding: 20px 50px;
  overflow: visible; /* 子のスクロールを殺さない */
  z-index: 10000;

  @media (max-width: 961px) {
    width: 95%;
    padding: 5px;
  }
`;

const CloseButton = styled.button`
  position: absolute; top: 10px; right: 10px;
  background: none; border: none;
  font-size: 1.8em; line-height: 1; cursor: pointer;
  z-index: 10001; pointer-events: auto;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
`;

const ModalBody = styled.div`
  padding: 20px; font-size: 1em; color: #333;
  overflow-y: auto;
  max-height: calc(90vh - 80px);
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  img { max-width: 100%; height: auto; border-radius: 10px; }

  .section-title, .sub-title { font-weight: 900; color: #EC407A; padding-bottom: 15px; border-bottom: 3px solid #EC407A; }
  .section-title { width: 4em; font-size: 1.6em; }
  .sub-title { width: 9em; font-size: 1.6em; padding-top: 25px; }
  .tiket-buttom { display: inline-block; background: #FFCA28; color: #000; padding: 12px 30px; border-radius: 30px; font-weight: bold; text-decoration: none; margin: 10px 0; }
  .notice-list { padding-left: 0; list-style-type: none; font-size: .7em; color: #000; }
  .notice-list .notice-item { padding-left: 1.2em; text-indent: -1.2em; }
  .notice-list .notice-item::before { content: "※"; color: #000; margin-right: 5px; }
  .info-list { padding-left: 0; list-style-type: none; font-size: 1em; color: #000; }
  .info-list br {display: none;}
  .info-list .info-item { padding-left: 1.2em; text-indent: -1.2em; }
  .link-underline { color: #1a0dab; text-decoration: underline; cursor: pointer; }
`;
